import React, { useState, useEffect } from "react";
import { Modal, Select, Typography, message, Spin } from "antd";
import NotificationsTemplate from "../../../../models/notificationsTemplate/notificaionsTemplate";
import BaseService from "../../../../service/base.service";
import { AudienceType } from "./notificationConstants";
import BaseUserInfo from "../../../../models/users/BaseUserInfo";
import notify from "../../../../components/common/notificationHelper";
import { useNavigate } from 'react-router-dom'; // for React Router v6
import { useAppDispatch } from "../../../../hooks/redux-hooks";
import { logout } from "../../../../slices/authSlice";
import { useIsMobile } from "../../../../hooks/useIsMobile";

interface SendNotificationModalProps {
    visible: boolean;
    template: NotificationsTemplate | null;
    onClose: () => void;
}

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({
    visible,
    template,
    onClose,
}) => {
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<{ label: string; value: number }[]>([]);
    const [fetching, setFetching] = useState(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const isAll = template?.audienceType === AudienceType.ALL_USERS;

    // 🔹 Reset selection & options when modal opens/closes or template changes
    useEffect(() => {
        if (visible) {
            setSelectedUsers([]);
            setOptions([]);
        }
    }, [visible, template]);

    const handleSearch = async (query: string) => {
        if (!query.trim()) return;
        setFetching(true);
        try {
            const rp = await BaseService.getAll<BaseUserInfo>(`/users/search?searchTerm=${encodeURIComponent(query)}`);
            if (rp.Status && rp.Data) {
                setOptions(
                    rp.Data.map((u: BaseUserInfo) => ({
                        label: u.name,
                        value: u.id,
                    }))
                );
            } else {
                setOptions([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            setOptions([]);
        } finally {
            setFetching(false);
        }
    };

    const handleSend = async () => {
        if (!template) return;
        if (!isAll && selectedUsers.length === 0) {
            message.warning("Please select at least one user.");
            return;
        }
        try {
            setLoading(true);
            if (isAll) {
                await BaseService.create(`/notifications/send/all/${template.id}`, {});
                notify({
                    title: 'Notification sent successfully',
                    description: `${template.title} Notification has been send to all users.`,
                    variant: 'success',
                });
            } else {
                if (selectedUsers.length === 0) {
                    message.warning("Please select at least one user.");
                    return;
                }
                // Send to specific users
                const payload = {
                    templateId: template.id,
                    userIds: selectedUsers,
                };
                await BaseService.create("/notifications/send/some-users", payload);
                notify({
                    title: 'Notification sent successfully',
                    description: `${template.title} Notification has been send to the selected users.`,
                    variant: 'success',
                });
            }
            onClose();
        } catch (err) {
            notify({
                title: 'Error sending Notification',
                description: `Failed to send Notification ${template.title}.`,
                variant: 'error',
            });
            console.error("Send error:", err);
            message.error("Failed to send notification.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await dispatch(logout()).unwrap();
            navigate("/login");
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Modal
            open={visible}
            title={`Send Notification: ${template?.title || "Notification"}`}
            onCancel={onClose}
            onOk={handleSend}
            okText="Send"
            confirmLoading={loading}
            width={isMobile ? "calc(100vw - 24px)" : 500}
            okButtonProps={{ disabled: !isAll && selectedUsers.length === 0 }}
        >
            {!template ? (
                <Typography.Text type="secondary">No template selected</Typography.Text>
            ) : isAll ? (
                <Typography.Text>
                    This notification will be sent to <b>all users</b>. Are you sure?
                </Typography.Text>
            ) : (
                <>
                    <Typography.Text>Select users to send the notification to:</Typography.Text>
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="Search and select users"
                        style={{ width: "100%", marginTop: 10 }}
                        value={selectedUsers}
                        onChange={setSelectedUsers}
                        onSearch={handleSearch}
                        filterOption={false}
                        notFoundContent={fetching ? <Spin size="small" /> : "No results"}
                        options={options}
                    />
                </>
            )}
        </Modal>
    );
};

export default SendNotificationModal;
